import { computed, reactive } from 'vue'

import { getMe } from '@/api/account'
import { apexUrl, spaceSlug, spaceUrl } from '@/lib/host'
import {
  ApiError,
  clearLoginFragment,
  describeLoginError,
  endSession,
  hasAccessToken,
  readLoginFragment,
  refreshSession,
  setAccessToken,
} from '@/lib/http'
import type { Account, MemberRole, Space } from '@/lib/types'

export type SessionStatus = 'idle' | 'loading' | 'ready' | 'anonymous' | 'error'

/**
 * One session for the whole app.
 *
 * A module-level singleton rather than a store library: the state is small
 * (account, memberships, status) and read by every page, so Pinia would add a
 * dependency and indirection without buying anything.
 */
const state = reactive({
  status: 'idle' as SessionStatus,
  account: null as Account | null,
  spaces: [] as Space[],
  /** A failure while loading the session itself. */
  error: '',
  /** A failure reported by the OAuth callback, kept separate so it can be
   *  shown on the landing screen without looking like a crash. */
  loginError: '',
})

/** The space whose slug matches the current hostname. */
const currentSpace = computed<Space | null>(
  () => state.spaces.find((s) => s.url === spaceSlug) ?? null,
)

/** The caller's role in this space, if the session carries one. */
const role = computed<MemberRole | null>(() => currentSpace.value?.role ?? null)

/**
 * Capability gates, mirroring the backend's rank table
 * (dblogic/member_role.go). They decide what the console *offers*; the API
 * decides what it *allows*, so a stale client cannot exceed its role.
 */
const isOwner = computed(() => role.value === 'owner')
/** Owner or admin: manage members, keys and the space's name/settings. */
const canManage = computed(() => role.value === 'owner' || role.value === 'admin')
/** Owner, admin or manager: read every member's usage and analytics. */
const canViewAll = computed(() => canManage.value || role.value === 'manager')
/** Owner or admin: read the funding ledger — the Billing screen. */
const canViewBilling = computed(() => canManage.value)

const isReady = computed(() => state.status === 'ready')

let inFlight: Promise<void> | null = null

/**
 * Resolve the session once, on first use.
 *
 * Concurrent callers share one promise so a page refresh does not fire several
 * refreshes at once — which would rotate the token repeatedly and invalidate
 * the sibling attempts.
 */
function bootstrap(): Promise<void> {
  if (inFlight) return inFlight
  if (state.status === 'ready' || state.status === 'anonymous') return Promise.resolve()
  inFlight = runBootstrap().finally(() => {
    inFlight = null
  })
  return inFlight
}

async function runBootstrap(): Promise<void> {
  state.status = 'loading'

  // 1. A fragment means we have just returned from Google.
  const fragment = readLoginFragment()
  if (fragment) {
    clearLoginFragment()
    if (fragment.error) {
      state.loginError = describeLoginError(fragment.error)
      state.status = 'anonymous'
      return
    }
    if (fragment.accessToken) {
      setAccessToken(fragment.accessToken)
    }
  }

  // 2. Either way, make sure we hold an access token. The refresh token rides an
  //    HttpOnly cookie shared by every space subdomain, which is what makes one
  //    sign-in work across all of them.
  if (!hasAccessToken()) {
    const refreshed = await refreshSession()
    if (!refreshed) {
      state.status = 'anonymous'
      return
    }
  }

  // 3. Load the account and its memberships.
  try {
    const me = await getMe()
    state.account = me.account
    state.spaces = me.spaces
    state.error = ''
    state.status = 'ready'
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      state.status = 'anonymous'
      return
    }
    state.error = err instanceof Error ? err.message : 'Could not load your session.'
    state.status = 'error'
  }
}

/** Re-fetch the account and memberships (after creating a space, for example). */
async function reload(): Promise<void> {
  const me = await getMe()
  state.account = me.account
  state.spaces = me.spaces
  state.status = 'ready'
}

/**
 * Send the browser to Google, returning here afterwards.
 *
 * Kept argument-less on purpose: it is bound directly as a click handler in
 * several views, and an optional parameter would make Vue pass the click event
 * as that parameter.
 */
function signIn(): void {
  signInTo(`${window.location.pathname}${window.location.search}`)
}

/**
 * Same, but returning to a specific same-origin path.
 *
 * The apex front page uses this to return to `/login`, so the post-sign-in hop
 * is decided in one place rather than duplicated as fragment handling on a
 * marketing page.
 */
function signInTo(returnTo: string): void {
  state.loginError = ''
  window.location.href = `/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`
}

async function signOut(): Promise<void> {
  await endSession()
  state.account = null
  state.spaces = []
  state.status = 'anonymous'
  window.location.href = apexUrl('/')
}

export type Destination = { kind: 'space'; url: string } | { kind: 'create' }

/**
 * Where to send someone right after they authenticate.
 *
 * Prefers the space they are already looking at, then one they own, then any.
 * With no memberships at all, the create-space page — which is exactly the
 * onboarding rule: an existing space if there is one, otherwise create one.
 */
function destinationAfterAuth(preferSlug: string | null = spaceSlug): Destination {
  if (state.spaces.length === 0) return { kind: 'create' }
  const chosen =
    state.spaces.find((s) => s.url === preferSlug) ??
    state.spaces.find((s) => s.role === 'owner') ??
    state.spaces[0]
  return { kind: 'space', url: spaceUrl(chosen.url, '/members') }
}

/** Navigate to a space dashboard. Cross-origin, so this is a full page load. */
function goToSpace(space: Space, path = '/members'): void {
  window.location.href = spaceUrl(space.url, path)
}

export function useSession() {
  return {
    state,
    currentSpace,
    role,
    isOwner,
    canManage,
    canViewAll,
    canViewBilling,
    isReady,
    bootstrap,
    reload,
    signIn,
    signInTo,
    signOut,
    destinationAfterAuth,
    goToSpace,
  }
}
